import React, { useState, useCallback, useEffect } from 'react';
import { useCaretPosition } from '../common/useCaretPosition';
import { useKeyboardNavigation } from '../events/useKeyboardNavigation';
import { useDebounce } from '../common/useDebounce';
import type { User } from '../../utils/userUtils';
import { searchUsersForMentions } from '../../utils/userUtils';
import { MENTION_REGEX } from './parsingConstants';

// Type for input/textarea element refs since InputComponent was removed
type InputElementRef = React.RefObject<HTMLInputElement | HTMLTextAreaElement>;

export interface Mention {
  id: string;
  user: User;
  start: number;
  end: number;
  display: string;
}

export interface MentionPopupState {
  isOpen: boolean;
  query: string;
  position: {
    x: number;
    y: number;
    maxHeight?: number;
    placement: 'above' | 'below';
  };
  triggerStart: number;
}

/**
 * Custom hook for managing @mentions functionality
 */
export function useMentions(
  value: string,
  onChange: (value: string) => void,
  inputRef: InputElementRef,
  debounceDelay: number = 300,
  usersApiEndpoint: string,
  maxResults: number = 100
) {
  const [users, setUsers] = useState<User[]>([]);
  const [mentions, setMentions] = useState<Mention[]>([]);
  const [loading, setLoading] = useState(false);
  const [popupState, setPopupState] = useState<MentionPopupState>({
    isOpen: false,
    query: '',
    position: { x: 0, y: 0, maxHeight: 300, placement: 'below' },
    triggerStart: 0
  });

  const { updateCaretPosition, getCaretCoordinates } =
    useCaretPosition(inputRef);
  const debouncedQuery = useDebounce(popupState.query, debounceDelay);

  // Fetch users using reusable utility function
  const fetchUsers = useCallback(
    async (query: string) => {
      if (!query.trim()) {
        setUsers([]);
        setLoading(false);
        return;
      }

      setLoading(true);
      try {
        const users = await searchUsersForMentions(
          query,
          usersApiEndpoint,
          maxResults
        );
        setUsers(users);
      } catch (error) {
        setUsers([]);
      } finally {
        setLoading(false);
      }
    },
    [usersApiEndpoint, maxResults]
  );

  // Debounced API call - FIXED VERSION
  useEffect(() => {
    // Only fetch if debouncedQuery matches current popup query
    if (
      debouncedQuery &&
      popupState.isOpen &&
      debouncedQuery === popupState.query
    ) {
      fetchUsers(debouncedQuery);
    }
  }, [debouncedQuery, popupState.isOpen, popupState.query, fetchUsers]);

  // Update popup position function
  const updatePopupPosition = useCallback(() => {
    if (popupState.isOpen && popupState.triggerStart !== undefined) {
      const coordinates = getCaretCoordinates(popupState.triggerStart);
      setPopupState(prev => ({
        ...prev,
        position: coordinates
      }));
    }
  }, [popupState.isOpen, popupState.triggerStart, getCaretCoordinates]);

  // Handle scroll events to update popup position
  useEffect(() => {
    if (!popupState.isOpen) return;

    let scrollTimeout: ReturnType<typeof setTimeout>;
    let isUpdating = false;

    const handleScroll = () => {
      // Use requestAnimationFrame for smooth updates
      if (!isUpdating) {
        isUpdating = true;
        requestAnimationFrame(() => {
          updatePopupPosition();
          isUpdating = false;
        });
      }
    };

    const handleResize = () => {
      // Throttle resize updates
      clearTimeout(scrollTimeout);
      scrollTimeout = setTimeout(() => {
        updatePopupPosition();
      }, 100);
    };

    // Add scroll listeners to window (capture phase catches all scroll events)
    window.addEventListener('scroll', handleScroll, true);
    window.addEventListener('resize', handleResize);

    return () => {
      clearTimeout(scrollTimeout);
      window.removeEventListener('scroll', handleScroll, true);
      window.removeEventListener('resize', handleResize);
    };
  }, [popupState.isOpen, updatePopupPosition]);

  // Keyboard navigation
  const { selectedIndex, setSelectedIndex, handleKeyDown } =
    useKeyboardNavigation(
      users,
      user => insertMention(user),
      popupState.isOpen
    );

  // Detect @ mentions
  const detectMention = useCallback(
    (text: string, caretPos: number) => {
      const beforeCaret = text.substring(0, caretPos);
      const atIndex = beforeCaret.lastIndexOf('@');

      if (atIndex === -1) return null;

      // Check if @ is at start or preceded by whitespace
      const charBeforeAt = atIndex > 0 ? beforeCaret[atIndex - 1] : ' ';
      if (
        charBeforeAt !== ' ' &&
        charBeforeAt !== '\n' &&
        charBeforeAt !== '\t'
      ) {
        return null;
      }

      // Check if this @ belongs to an already-inserted mention
      // If cursor is past a completed mention, don't reopen popup
      const existingMention = mentions.find(
        m => atIndex >= m.start && atIndex < m.end
      );

      if (existingMention && caretPos > existingMention.end) {
        // User has moved past the completed mention
        return null;
      }

      const afterAt = beforeCaret.substring(atIndex + 1);

      // Stop if query is too long or has too many word parts
      const spaceCount = (afterAt.match(/ /g) || []).length;
      if (afterAt.length > 15 || spaceCount > 2) {
        return null;
      }

      // Only terminate on newline or tab (allow spaces for searching)
      if (afterAt.includes('\n') || afterAt.includes('\t')) {
        return null;
      }

      return {
        start: atIndex,
        query: afterAt
      };
    },
    [mentions]
  );

  // Handle input changes
  const handleInputChange = useCallback(
    (newValue: string, newCaretPos: number) => {
      const mentionMatch = detectMention(newValue, newCaretPos);

      if (mentionMatch) {
        // Use the "@" symbol position for popup alignment
        const coordinates = getCaretCoordinates(mentionMatch.start);
        setPopupState({
          isOpen: true,
          query: mentionMatch.query,
          position: coordinates,
          triggerStart: mentionMatch.start
        });
      } else {
        setPopupState(prev => ({ ...prev, isOpen: false }));
      }

      // Update mentions list when text changes
      updateMentionPositions(newValue);
    },
    [detectMention, getCaretCoordinates]
  );

  // Insert mention into text
  const insertMention = useCallback(
    (user: User) => {
      const beforeMention = value.substring(0, popupState.triggerStart);
      // Calculate the end position of the query (@query) to properly replace it
      const queryEndPosition =
        popupState.triggerStart + 1 + popupState.query.length;
      const afterMention = value.substring(queryEndPosition);
      const mentionText = `@${user.username}`;

      const newValue = beforeMention + mentionText + afterMention;
      const newCaretPos = popupState.triggerStart + mentionText.length;

      // Add space after the mention only if afterMention doesn't already start with one
      const needsSpace =
        afterMention.length === 0 || !afterMention.startsWith(' ');
      const newValueWithSpace =
        beforeMention + mentionText + (needsSpace ? ' ' : '') + afterMention;

      // Create new mention object
      const newMention: Mention = {
        id: `mention-${Date.now()}-${user.id}`,
        user,
        start: popupState.triggerStart,
        end: popupState.triggerStart + mentionText.length,
        display: mentionText
      };

      // Add the new mention and immediately update all positions
      setMentions(prev => {
        // Add the new mention
        const withNewMention = [...prev, newMention];

        // Immediately recalculate all positions based on the new text WITH SPACE
        // This ensures all mentions (including the new one) have correct positions
        const usernameToMentions = new Map<string, Mention[]>();
        withNewMention.forEach(mention => {
          const username = mention.user.username;
          if (!usernameToMentions.has(username)) {
            usernameToMentions.set(username, []);
          }
          usernameToMentions.get(username)!.push(mention);
        });

        const usedMentionIds = new Set<string>();
        const updatedMentions: Mention[] = [];
        const timestamp = Date.now();

        MENTION_REGEX.lastIndex = 0;
        let match;
        let matchIndex = 0;

        while ((match = MENTION_REGEX.exec(newValueWithSpace)) !== null) {
          const username = match[1];
          const start = match.index;
          const end = match.index + match[0].length;
          const mentionText = match[0];

          const availableMentions = usernameToMentions.get(username) || [];
          const existingMention = availableMentions.find(
            m => !usedMentionIds.has(m.id)
          );

          if (existingMention) {
            usedMentionIds.add(existingMention.id);
            updatedMentions.push({
              id: `mention-${timestamp}-${existingMention.user.id}-${matchIndex}`,
              user: existingMention.user,
              start,
              end,
              display: mentionText
            });
          }

          matchIndex++;
        }

        return updatedMentions;
      });
      setPopupState(prev => ({ ...prev, isOpen: false }));

      // Use the value with space from the start
      onChange(newValueWithSpace);

      // Set caret position after insertion (with space if added)
      setTimeout(() => {
        if (inputRef.current) {
          // Position cursor after the space (if one was added)
          const caretPosition = newCaretPos + (needsSpace ? 1 : 0);
          inputRef.current.setSelectionRange(caretPosition, caretPosition);
        }
      }, 0);
    },
    [value, popupState.triggerStart, popupState.query, onChange, inputRef]
  );

  // Update mention positions when text changes
  const updateMentionPositions = useCallback((newValue: string) => {
    setMentions(prev => {
      // Quick check: if no previous mentions and text doesn't contain complete mentions, return empty
      if (prev.length === 0 && !/@[\w.]+/.test(newValue)) {
        return [];
      }

      // Create a map of usernames to arrays of existing mentions (handles duplicates)
      const usernameToMentions = new Map<string, Mention[]>();
      prev.forEach(mention => {
        const username = mention.user.username;
        if (!usernameToMentions.has(username)) {
          usernameToMentions.set(username, []);
        }
        usernameToMentions.get(username)!.push(mention);
      });

      // Track which previous mentions we've already matched
      const usedMentionIds = new Set<string>();

      // Scan the entire text for all mentions
      const updatedMentions: Mention[] = [];
      const processedPositions = new Set<string>();
      const timestamp = Date.now();

      // Reset regex state to ensure we start from the beginning
      MENTION_REGEX.lastIndex = 0;
      let match;
      let matchIndex = 0;

      while ((match = MENTION_REGEX.exec(newValue)) !== null) {
        const username = match[1];
        const start = match.index;
        const end = match.index + match[0].length;
        const mentionText = match[0];
        const positionKey = `${start}-${end}`;

        // Skip if we've already processed a mention at this position
        if (processedPositions.has(positionKey)) {
          continue;
        }
        processedPositions.add(positionKey);

        // Find an unused existing mention for this username
        const availableMentions = usernameToMentions.get(username) || [];
        const existingMention = availableMentions.find(
          m => !usedMentionIds.has(m.id)
        );

        if (existingMention) {
          // Mark this mention as used
          usedMentionIds.add(existingMention.id);

          // Update the mention with new positions but keep the user data
          // Generate new ID to ensure proper re-rendering
          updatedMentions.push({
            id: `mention-${timestamp}-${existingMention.user.id}-${matchIndex}`,
            user: existingMention.user,
            start,
            end,
            display: mentionText
          });
        }

        matchIndex++;
      }

      return updatedMentions;
    });
  }, []);

  // Close popup
  const closePopup = useCallback(() => {
    setPopupState(prev => ({ ...prev, isOpen: false }));
    setSelectedIndex(-1);
  }, [setSelectedIndex]);

  // Get mention at position
  const getMentionAtPosition = useCallback(
    (position: number) => {
      return mentions.find(
        mention => position >= mention.start && position < mention.end
      );
    },
    [mentions]
  );

  return {
    popupState,
    users,
    loading,
    mentions,
    selectedIndex,
    handleInputChange,
    handleKeyDown,
    insertMention,
    closePopup,
    updateCaretPosition,
    getMentionAtPosition
  };
}
